<template>
  <div class="widget-embed-modal-content">
    <div class="playlist-selections">
      <CustomInputValidation
        ref="horizontalPlaylistRef"
        v-model="selectedHorizontalPlaylist"
        class="playlist-group"
        :input-type="CustomInputType.Multiselect"
        :validation-object="validationTriggered && !selectedHorizontalPlaylist && !selectedVerticalPlaylist ? CustomInputValidator.InputMultiselectRequired : {}"
        :label="t('horizontal-playlist')"
        :asteriks="false"
        :show-error-message="false"
        :disabled="isInputsDisabled"
        :options="[]"
        :value-prop="'playlist_id'"
        :label-key="'playlist_name'"
        :async="getPlaylistsCached"
        :async-prop="{
          organization_id: store.state.user.user.organization_id,
          sort_by: 'playlist_name',
          sort_order: SortOrder.Ascending,
          list_only: true
        }"
        :spread="'playlists'"
        :label-key-with-id="true"
        :track-by="['playlist_id', 'playlist_name']"
        :delay="-1"
        :close-on-select="true"
        :resolve-on-load="true"
        :can-deselect="false"
        :can-clear="false"
        :clear-on-select="true"
        :clear-on-blur="true"
        :object="false"
        :searchable="true"
        :mode="'single'"
        :placeholder="t('select-playlist')"
        @change="selectPlaylist"
      />
      <CustomInputValidation
        ref="verticalPlaylistRef"
        v-model="selectedVerticalPlaylist"
        class="playlist-group"
        :input-type="CustomInputType.Multiselect"
        :validation-object="validationTriggered && !selectedHorizontalPlaylist && !selectedVerticalPlaylist ? CustomInputValidator.InputMultiselectRequired : {}"
        :label="t('vertical-playlist')"
        :asteriks="false"
        :show-error-message="false"
        :disabled="isInputsDisabled"
        :options="[]"
        :value-prop="'playlist_id'"
        :label-key="'playlist_name'"
        :async="getPlaylistsCached"
        :async-prop="{
          organization_id: store.state.user.user.organization_id,
          sort_by: 'playlist_name',
          sort_order: SortOrder.Ascending,
          list_only: true
        }"
        :spread="'playlists'"
        :label-key-with-id="true"
        :track-by="['playlist_id', 'playlist_name']"
        :delay="-1"
        :close-on-select="true"
        :resolve-on-load="true"
        :can-deselect="false"
        :can-clear="false"
        :clear-on-select="true"
        :clear-on-blur="true"
        :object="false"
        :searchable="true"
        :mode="'single'"
        :placeholder="t('select-playlist')"
        @change="selectPlaylist"
      />
    </div>
    <div v-if="showNoPlaylistWarning" class="no-playlist-warning-text">
      {{ t('at-least-one-playlist-warning') }}
    </div>
    <div class="code-section">
      <TextIconTooltip
        class="title" text-size="0.875rem" :text="t('widgets-get-code-and-embed-code-title')" :tooltip="t('embed-widget-code-info')"
        :teleport-to-body="true"
      />
      <div class="code-container" :class="{ disabled: !embedCode }">
        <pre>{{ embedCode || embedCodePlaceholder }}</pre>
        <div class="copy-button">
          <ActionButtons class="copy-icon" :actions="copyActions" :is-visible="Boolean(embedCode)" :is-dots-menu="false" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, nextTick, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { apiService } from '@/services'
import { CustomInputType, SortOrder, CustomModalProps } from '@/interfaces'
import { getPlayerCDNDomain } from '@/services/utils'
import * as CustomInputValidator from '@/data/models/inputOptions/input'
import CustomInputValidation from '@/components/common/custom-inputs/CustomInputValidation.vue'
import ActionButtons from '@/components/common/action-buttons/ActionButtons.vue'
import TextIconTooltip from '@/components/common/tooltips/TextIconTooltip.vue'
import { Playlists } from '@/services/playlist/types'

interface WidgetEmbedExtraProps {
  widgetId: string | number
  widgetName: string
}

interface WidgetEmbedModalProps extends CustomModalProps {
  extraProps?: WidgetEmbedExtraProps
}

const props = defineProps<WidgetEmbedModalProps>()
const store = useStore()
const { t } = useI18n()
const selectedHorizontalPlaylist = ref<string | number | null>(null)
const selectedVerticalPlaylist = ref<string | number | null>(null)
const embedCode = ref('')
const canCopy = ref(true)
const showNoPlaylistWarning = ref(false)
const validationTriggered = ref(false)
const horizontalPlaylistRef = ref()
const verticalPlaylistRef = ref()
const isInputsDisabled = computed(() => Boolean(embedCode.value))
const embedCodePlaceholder = t('embed-select-playlist-placeholder')

let playlistsCache: Promise<Playlists> | null = null

function getPlaylistsCached(params: { organization_id: number; sort_by: string; sort_order: string; list_only: boolean }) {
  if (!playlistsCache) {
    playlistsCache = apiService.playlist.getPlaylists(params)
  }
  return playlistsCache
}

onMounted(() => {
  store.dispatch('modal/setModalPayload', { getCode: generateEmbedCode, startEditing })
  store.dispatch('modal/setExtraProps', { embedCode: embedCode })
})
const copyActions = [
  {
    label: 'copy',
    handler: copyToClipboard,
    translationKey: 'copy'
  }
]

function copyToClipboard() {
  if (!embedCode.value) return
  navigator.clipboard.writeText(embedCode.value)
  if (canCopy.value) {
    canCopy.value = false
    store.dispatch('messages/addMessage', {
      message: t('library-embed-copy-clipboard'),
      type: 'success'
    })
    setTimeout(() => {
      canCopy.value = true
    }, 2500)
  }
}

function selectPlaylist() {
  if (showNoPlaylistWarning.value) showNoPlaylistWarning.value = false
}

async function generateEmbedCode() {
  const widgetId = props.extraProps?.widgetId
  if (!widgetId) return
  validationTriggered.value = true
  await nextTick()
  if (horizontalPlaylistRef.value) {
    horizontalPlaylistRef.value.validate()
  }
  if (verticalPlaylistRef.value) {
    verticalPlaylistRef.value.validate()
  }
  const isHorizontalPlaylistSelected = Boolean(selectedHorizontalPlaylist.value)
  const isVerticalPlaylistSelected = Boolean(selectedVerticalPlaylist.value)
  if (!selectedHorizontalPlaylist.value && !selectedVerticalPlaylist.value) {
    showNoPlaylistWarning.value = true
    embedCode.value = ''
    return
  }

  showNoPlaylistWarning.value = false
  validationTriggered.value = false
  const baseUrl = getPlayerCDNDomain()
  const scriptStart = '<script data-cfasync="false" async type="text/javascript" src="'
  const scriptEnd = '"></scr' + 'ipt>'

  const qs = new URLSearchParams()
  if (isHorizontalPlaylistSelected) qs.append('pid', String(selectedHorizontalPlaylist.value))
  if (isVerticalPlaylistSelected) qs.append('vpid', String(selectedVerticalPlaylist.value))

  const url = `${baseUrl}/init/${String(widgetId)}.js${qs.toString() ? `?${qs}` : ''}`
  embedCode.value = `${scriptStart}${url}${scriptEnd}`
}

const startEditing = () => {
  embedCode.value = ''
  validationTriggered.value = false
}
</script>

<style lang="scss" scoped>
.widget-embed-modal-content {
  width: 640px;
  display: flex;
  margin-inline: 2px;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;

  .playlist-selections {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    .playlist-group {
      width: 100%;
      &.custom-input {
        margin: 0 !important;
      }
    }
  }
  .code-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
    .title {
      color: $bodyText;
    }
    .code-container {
      display: flex;
      border: 1px solid $embedWidgetBorderColor;
      border-radius: 4px;
      overflow: hidden;
      padding: 16px 8px 16px 16px;
      align-items: flex-start;

      pre {
        width: 100%;
        margin: 0;
        min-height: 48px;
        font-size: 12px;
        color: $embedWidgetCodeColor;
        line-height: 1.4;
        white-space: pre-wrap;
        word-break: break-all;
      }
      .copy-button {
        display: flex;
        align-items: flex-start;
        padding: 0.5rem;
        width: fit-content;

        :deep(.actions-container) {
          padding: 0;
        }
      }
    }
  }
  .no-playlist-warning-text {
    color: $error-custom-validation;
    font-size: 0.875rem;
    font-weight: 400;
  }
}
</style>
